import Image from "next/image";
import React from "react";
import styles from "./SummaryCard.module.css";
import { getPackageConfig } from "components/ContentfulRenderer/ContentfulPackages/getPackageConfig";
import { getDateFormat, getOrg, isOrg } from "utils/site";
import {
  getDiscountDisplayValue,
  YEARLY_BILLING_INTERVAL,
  isUnlimitedLimit,
} from "utils/insurance";
import classNames from "classnames";
import {
  Accordion,
  AccordionItem,
  AccordionPanel,
  AccordionTitle,
} from "ui-kit";
import { getAmountToPay } from "utils/insurance/getAmountToPay";
import { getDiscountDisclaimerTranslationKey } from "components/Signup/components/DiscountDisclaimer/DiscountDisclaimer";

import Wave from "components-shared/Wave";
import {
  DiscountType,
  ProductEnum,
} from "../../../../../__generated__/globalTypes";
import { useWebTranslation } from "translations";
import { formatCurrency, formatDate } from "utils/utils";
import { Typography } from "ui-kit/Typography";
import Gutter from "ui-kit/Gutter";
import { FlexCol, FlexRow } from "ui-kit/Layout/Flex";
import {
  SummaryBlock,
  SummaryItem,
  SummaryTable,
} from "components/SummaryTable/SummaryTable";
import Colors from "ui-kit/colors";
import {
  getCurrentDraftForAnimal,
  useAllValidAnimals,
} from "components/Signup/store/selectors/animals";
import { SignUpProductEnum } from "components/Signup/flow/SignupFlow/types";
import { useSignupDispatch } from "components/Signup/store/hooks";
import { editDraftsByAnimalId } from "components/Signup/store/slices/animals";
import { AnimalButtons } from "../AnimalButtons";
import { BillingIntervalText } from "../BillingIntervalText";

const getDeductibleBaseOnProduct = ({
  product,
  deductible,
  copay,
}: {
  product: SignUpProductEnum;
  deductible?: number;
  copay?: number | null;
}): string | null => {
  if ((!deductible && deductible !== 0) || (!copay && copay !== 0)) {
    return null;
  }

  if (product === SignUpProductEnum.liability) {
    return `${formatCurrency(deductible / 100)}`;
  } else {
    switch (getOrg()) {
      case "swe":
        return `${formatCurrency(deductible / 100)} + ${copay * 100} %`;
      case "ger": {
        return `${copay * 100} %`;
      }
      case "fra": {
        return `${(1 - copay) * 100} %`;
      }
    }
  }
};

type SummaryCardProps = {
  withRibbon?: boolean;
  showButtons?: boolean;
};

export const SummaryCard = ({ withRibbon, showButtons }: SummaryCardProps) => {
  const { t } = useWebTranslation();
  const dispatch = useSignupDispatch();

  const animals = useAllValidAnimals();

  return (
    <>
      {!withRibbon && animals.length < 2 ? (
        <>
          <Typography variant="h4" color="grey500">
            <strong>{t("insurance.summary.title")}</strong>
          </Typography>
        </>
      ) : null}

      <Accordion collapsible>
        {animals.map((animal) => {
          return animal.insurances.map((insurance) => {
            const {
              billingInterval,
              preventiveCareAddOnLimit,
              preexistingConditions,
              product,
              lifeLimit,
              lifeUtilityAddOn,
              breedersAddOn,
            } = insurance;

            const currentDraft = getCurrentDraftForAnimal(animal);

            if (!currentDraft) {
              console.error("could not find current draft for animal");
              return null;
            }

            const packagesConfig = getPackageConfig({
              product:
                insurance.product === SignUpProductEnum.liability
                  ? ProductEnum.liability
                  : (animal.species as unknown as ProductEnum),
            });
            const packageConfig = packagesConfig?.packages.find(
              (p) =>
                p.limit === currentDraft?.limit &&
                (p.deductible === undefined
                  ? true
                  : p.deductible === currentDraft?.deductible)
            );

            const amountToPay = getAmountToPay({
              ...currentDraft,
              billingInterval,
            });

            const amountToPayYearly = getAmountToPay({
              ...currentDraft,
              billingInterval: YEARLY_BILLING_INTERVAL,
            });

            return (
              <AccordionItem
                key={animal.id}
                className={classNames(
                  styles.accordionItem,
                  withRibbon ? styles.withRibbon : undefined
                )}
              >
                <AccordionTitle className={styles.accordionTitle}>
                  <FlexRow alignItems="center">
                    {!withRibbon && packageConfig && (
                      <>
                        <Image
                          src={packageConfig.img}
                          alt={t(packageConfig.title)}
                          className={styles.image}
                          width={32}
                          height={42}
                        />
                        <Gutter size={2} horizontal />
                      </>
                    )}
                    <FlexCol>
                      <Typography variant="body2">
                        <strong>{animal.name}</strong>
                      </Typography>
                      <Gutter size={1} />

                      <FlexCol>
                        <FlexRow alignItems="baseline" style={{ gap: 8 }}>
                          {amountToPay.old ? (
                            <>
                              <Typography variant="body3" color="grey500">
                                <s>{formatCurrency(amountToPay.old)}</s>
                              </Typography>
                            </>
                          ) : null}
                          <Typography variant="body2">
                            <strong>{formatCurrency(amountToPay?.new)}</strong>
                          </Typography>
                          <BillingIntervalText color="grey500" />
                        </FlexRow>
                      </FlexCol>
                      <Gutter size={1} />
                      <Typography color="grey500" variant="body3">
                        {formatDate(
                          insurance.startDate ?? undefined,
                          getDateFormat()
                        )}
                      </Typography>
                    </FlexCol>
                  </FlexRow>
                </AccordionTitle>
                <AccordionPanel>
                  <div className={styles.container}>
                    <SummaryTable>
                      <SummaryBlock title={t("global.coverage")}>
                        {packageConfig && (
                          <SummaryItem
                            title={t("global.insurance_package")}
                            value={
                              <strong>
                                {`${t(packageConfig.title)} · ${
                                  isUnlimitedLimit(currentDraft.limit)
                                    ? t("global.unlimited_coverage")
                                    : formatCurrency(currentDraft.limit / 100)
                                }`}
                              </strong>
                            }
                          />
                        )}
                        {insurance.policy?.policyNumber ? (
                          <SummaryItem
                            title={t("global.insurance_number")}
                            value={insurance.policy.policyNumber}
                          />
                        ) : null}
                        <SummaryItem
                          title={
                            isOrg("fra")
                              ? t("global.copay")
                              : t("global.deductible")
                          }
                          value={getDeductibleBaseOnProduct({
                            product: product!,
                            copay: currentDraft.copay as number | undefined,
                            deductible: currentDraft.deductible,
                          })}
                        />
                        {isOrg("fra") &&
                        preventiveCareAddOnLimit !== 0 &&
                        preventiveCareAddOnLimit ? (
                          <SummaryItem
                            title={t("insurance.preventive_care_package_title")}
                            value={formatCurrency(
                              preventiveCareAddOnLimit / 100
                            )}
                          />
                        ) : null}

                        {isOrg("swe") && (
                          <>
                            <SummaryItem
                              title={t("global.life_insurance")}
                              value={
                                lifeLimit && lifeLimit > 0
                                  ? formatCurrency(lifeLimit)
                                  : t("global.no")
                              }
                            />
                            {Boolean(lifeUtilityAddOn) ? (
                              <SummaryItem
                                title={t("global.life_utility_add_on")}
                                value={t(
                                  `choose_life_insurance.life_insurance.utility.option.${lifeUtilityAddOn}`
                                )}
                              />
                            ) : null}
                            <SummaryItem
                              title={t(
                                "signup.step.addons.preexisting_conditions.title"
                              )}
                              value={
                                preexistingConditions
                                  ? t("global.yes")
                                  : t("global.no")
                              }
                            />
                          </>
                        )}
                        {breedersAddOn && (
                          <SummaryItem
                            title={t("global.breeders_add_on")}
                            value={t("global.yes")}
                          />
                        )}
                      </SummaryBlock>

                      {amountToPay.fees.length > 0 && (
                        <SummaryBlock title={t("global.discount")}>
                          {amountToPay.fees.map((fee, index) => {
                            if (!fee.total || !fee.discountName) {
                              return null;
                            }

                            return (
                              <SummaryItem
                                key={fee.total}
                                icon="discount-badge"
                                title={
                                  fee.discountType === DiscountType.MultiPet
                                    ? t("discounts.multipet_display_text")
                                    : fee.discountName
                                }
                                value={formatCurrency(
                                  getDiscountDisplayValue({
                                    fee,
                                    billingInterval,
                                  })
                                )}
                                description={t(
                                  getDiscountDisclaimerTranslationKey({
                                    duration:
                                      billingInterval ===
                                      YEARLY_BILLING_INTERVAL
                                        ? 12
                                        : fee.maxInvoicesSpread,
                                    type: fee.discountType,
                                  }),
                                  { months: fee.maxInvoicesSpread ?? 12 }
                                )}
                                highlightValue
                              />
                            );
                          })}
                        </SummaryBlock>
                      )}
                      <SummaryBlock
                        title={t("insurance.about", { name: name ?? "" })}
                      >
                        <SummaryItem
                          title={t("global.birthdate")}
                          value={
                            formatDate(
                              animal.birthday ?? undefined,
                              getDateFormat()
                            ) ?? ""
                          }
                        />
                        <SummaryItem
                          title={t("global.breed")}
                          value={animal.breed?.name ?? ""}
                        />
                        <SummaryItem
                          title={t("global.sex")}
                          value={`${t(
                            animal.sex === "female"
                              ? "{{species, animal_pronoun(sex: female)}}"
                              : "{{species, animal_pronoun(sex: male)}}",
                            {
                              species: animal.species ?? "",
                            }
                          )}${
                            animal.sexSituation === "castrated"
                              ? `, ${t(
                                  `global.neutered_${animal.sex}`
                                ).toLowerCase()}`
                              : ""
                          }`}
                        />
                      </SummaryBlock>
                    </SummaryTable>
                    {withRibbon ? (
                      <div className={styles.ribbonContainer}>
                        <Wave
                          color={Colors.offwhite}
                          style={{
                            position: "absolute",
                            top: 0,
                          }}
                        />
                      </div>
                    ) : null}
                  </div>
                </AccordionPanel>
                {showButtons ? (
                  <AnimalButtons
                    id={animal.id}
                    showDeleteButton={animals.length > 1}
                    onEdit={() => dispatch(editDraftsByAnimalId(animal.id))}
                  />
                ) : null}
              </AccordionItem>
            );
          });
        })}
      </Accordion>
    </>
  );
};
